/**
 * Investigation orchestrator — ARCHITECTURE.md section 5.2.
 *
 * State machine with explicit transitions and programmatic exit criteria.
 * AI NEVER controls transitions — every gate is deterministic code.
 *
 * State flow:
 * INIT → DECOMPOSE → COLLECT → EXTRACT → VALIDATE → GRAPH_BUILD →
 * CORROBORATE → SYNTHESIZE → REVIEW
 *
 * Each transition:
 * 1. Persists state to SQLite
 * 2. Emits event bus event
 * 3. Logs to investigation_log
 *
 * Investigation is resumable after app restart (loads from SQLite).
 */

import { eventBus } from '@/core/events';
import { getDeskConfig } from '@/config/desk-config';
import { TokenTracker, createLLMAdapter } from '@/intelligence/ai/llm-adapter';
import type { LLMAdapter } from '@/intelligence/ai/llm-adapter';
import { createRelevanceFilter } from '@/intelligence/ai/relevance-filter';
import { createEntityExtractor } from '@/intelligence/ai/entity-extractor';
import { createRelationshipIdentifier } from '@/intelligence/ai/relationship-identifier';
import { validateRelationships } from '@/intelligence/ai/validation';
import type {
  Investigation,
  InvestigationState,
  InvestigationTierName,
  InvestigationTierConfig,
  CollectionPlan,
  ScopeContract,
  CoverageReport,
  CoverageParameter,
} from './types';
import {
  INVESTIGATION_TIERS as TIERS,
} from './types';
import {
  createInvestigation,
  getInvestigation,
  updateInvestigation,
  addLogEntry,
  getCollectionTasks,
  createCollectionTask,
  linkEntityToInvestigation,
  getInvestigationEntityIds,
} from './investigation-store';
import { createResourceManager } from './resource-manager';
import type { ResourceManager, ResourceCheck } from './resource-manager';
import { buildCollectionPlan } from './task-builder';
import { decomposeQuery } from './query-decomposer';
import { runProgressivePipeline } from './progressive-pipeline';
import { runCorroboration } from './corroboration';
import { entityStore } from '@/intelligence/entities/entity-store';
import type { Entity } from '@/intelligence/entities/types';
import { registerInvestigationScope, unregisterInvestigationScope } from './analysis-bridge';
import { auditProvenance } from '@/intelligence/validation/provenance-audit';
import { identifyEnrichmentQueries, runEnrichment } from './entity-enrichment';

// ---------------------------------------------------------------------------
// Transition map — deterministic gate definitions
// ---------------------------------------------------------------------------

/**
 * Valid state transitions. Each entry defines:
 * - from → to: the allowed transition
 * - gate: a description of the exit criteria (checked in code)
 *
 * The agent/LLM NEVER decides when to transition.
 */
const VALID_TRANSITIONS: Array<[InvestigationState, InvestigationState]> = [
  ['init', 'decompose'],
  ['decompose', 'collect'],
  ['collect', 'extract'],
  ['extract', 'validate'],
  ['validate', 'graph_build'],
  ['graph_build', 'corroborate'],
  ['corroborate', 'synthesize'],
  ['synthesize', 'review'],
  // Error transitions — any state can go to error
  ['init', 'error'],
  ['decompose', 'error'],
  ['collect', 'error'],
  ['extract', 'error'],
  ['validate', 'error'],
  ['graph_build', 'error'],
  ['corroborate', 'error'],
  ['synthesize', 'error'],
  // Resource limit — can skip to review from collect/extract/validate
  ['collect', 'review'],
  ['extract', 'review'],
  ['validate', 'review'],
  ['graph_build', 'review'],
  ['corroborate', 'review'],
];

function isValidTransition(from: InvestigationState, to: InvestigationState): boolean {
  return VALID_TRANSITIONS.some(([f, t]) => f === from && t === to);
}

// ---------------------------------------------------------------------------
// Orchestrator interface
// ---------------------------------------------------------------------------

export interface InvestigationOrchestrator {
  /** The investigation ID being managed. */
  readonly investigationId: string;

  /** Current investigation state. */
  getState(): InvestigationState;

  /** Get the full investigation record. */
  getInvestigation(): Promise<Investigation | null>;

  /** Get the resource manager for this investigation. */
  getResourceManager(): ResourceManager;

  /** Get the token tracker for this investigation. */
  getTokenTracker(): TokenTracker;

  /**
   * Start the investigation (INIT → DECOMPOSE).
   * Gate: desk config loaded, ≥1 source enabled.
   */
  start(): Promise<void>;

  /**
   * Confirm the scope contract (DECOMPOSE → COLLECT).
   * Called after analyst reviews decomposition.
   * Gate: analyst confirmed scope contract.
   */
  confirmScope(scopeContract: ScopeContract): Promise<void>;

  /**
   * Run the collection phase. Called by the executor (S7).
   * For now, marks collection as complete for the state machine.
   * Gate: all tasks completed/errored, none pending.
   */
  completeCollection(): Promise<void>;

  /**
   * Run progressive collection + extraction pipeline (S7).
   * Combines COLLECT → EXTRACT → VALIDATE in a progressive flow:
   * each task's data flows through extraction as soon as it arrives.
   * Entities appear in the UI incrementally.
   */
  runCollectionAndExtraction(): Promise<void>;

  /**
   * Run extraction on collected data (COLLECT → EXTRACT → VALIDATE).
   * Gate: all entities have mandatory fields attempted.
   */
  runExtraction(): Promise<void>;

  /**
   * Build graph from validated entities (VALIDATE → GRAPH_BUILD).
   * Gate: all entities pass schema validation.
   */
  buildGraph(): Promise<void>;

  /**
   * Run corroboration pass (GRAPH_BUILD → CORROBORATE).
   * Gate: graph consistent, no orphan edges.
   */
  corroborate(): Promise<void>;

  /**
   * Run synthesis (CORROBORATE → SYNTHESIZE).
   * Gate: corroboration scores assigned.
   * Skipped if tier.synthesis = false.
   */
  synthesize(): Promise<void>;

  /**
   * Transition to review (SYNTHESIZE → REVIEW).
   * Gate: draft generated (or synthesis skipped).
   */
  completeToReview(): Promise<void>;

  /**
   * Force transition to review with current partial results.
   * Used when resource limits are hit.
   */
  forceReview(reason: string): Promise<void>;

  /**
   * Transition to error state.
   */
  transitionToError(error: string): Promise<void>;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export function createOrchestrator(
  investigationId: string,
  tierName: InvestigationTierName,
  initialState: InvestigationState = 'init',
  startTime?: Date,
): InvestigationOrchestrator {
  const tierConfig: InvestigationTierConfig = TIERS[tierName];
  const tokenTracker = new TokenTracker();
  const resourceManager = createResourceManager(investigationId, tierConfig, startTime);
  const adapter: LLMAdapter = createLLMAdapter(tokenTracker);

  let currentState: InvestigationState = initialState;

  // ------- Transition helper -------

  async function transition(to: InvestigationState, detail?: Record<string, unknown>): Promise<void> {
    const from = currentState;

    if (!isValidTransition(from, to)) {
      throw new Error(`Invalid state transition: ${from} → ${to}`);
    }

    currentState = to;

    // Persist to SQLite
    const updates: Parameters<typeof updateInvestigation>[1] = {
      state: to,
      tokenUsage: tokenTracker.totalTokens,
    };

    if (to === 'review') {
      updates.completedAt = new Date().toISOString();
    }

    await updateInvestigation(investigationId, updates);

    // Log to investigation_log
    await addLogEntry({
      investigationId,
      state: to,
      action: 'state_transition',
      detail: { from, to, ...detail },
    });

    // Emit event
    eventBus.emit('investigation:state', { investigationId, from, to });

    // Sync resource state
    const entityIds = await getInvestigationEntityIds(investigationId);
    resourceManager.updateGraphCounts(entityIds.length, 0); // Edge count updated during graph build
  }

  // ------- Resource check helper -------

  function checkResourcesOrForceReview(): ResourceCheck {
    const check = resourceManager.check();
    return check;
  }

  // ------- Public methods -------

  async function start(): Promise<void> {
    if (currentState !== 'init') {
      throw new Error(`Cannot start: investigation is in state '${currentState}', expected 'init'`);
    }

    // Gate: desk config loaded, ≥1 source enabled
    const investigation = await getInvestigation(investigationId);
    if (!investigation) throw new Error(`Investigation not found: ${investigationId}`);

    const deskConfig = getDeskConfig(investigation.deskId);
    if (!deskConfig) {
      await transitionToError(`Desk config not found: ${investigation.deskId}`);
      return;
    }

    const enabledSources = deskConfig.sources.filter(s => s.enabled);
    if (enabledSources.length === 0) {
      await transitionToError('No enabled sources in desk config');
      return;
    }

    // Transition to DECOMPOSE
    await transition('decompose', { enabledSources: enabledSources.length });

    // Run query decomposition (LLM call)
    try {
      const result = await decomposeQuery(investigation.rawQuery, deskConfig, adapter);

      tokenTracker.record(result.model, result.tokensUsed);
      resourceManager.recordTokens(result.tokensUsed);

      // Store decomposition result
      await updateInvestigation(investigationId, {
        decomposed: result.scopeContract,
      });

      // Log the decomposition
      await addLogEntry({
        investigationId,
        state: 'decompose',
        action: 'llm_call',
        detail: {
          purpose: 'query_decomposition',
          model: result.model,
          tokensUsed: result.tokensUsed,
        },
      });

      // Emit for UI — analyst must confirm before we proceed
      eventBus.emit('investigation:coverage-update', {
        investigationId,
        coverage: {
          parameters: [],
          overallPercentage: 0,
          sufficientForSynthesis: false,
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      await transitionToError(`Query decomposition failed: ${message}`);
    }
  }

  async function confirmScope(scopeContract: ScopeContract): Promise<void> {
    if (currentState !== 'decompose') {
      throw new Error(`Cannot confirm scope: investigation is in state '${currentState}', expected 'decompose'`);
    }

    // Store the confirmed scope contract (immutable after confirmation)
    await updateInvestigation(investigationId, {
      scopeContract,
    });

    await addLogEntry({
      investigationId,
      state: 'decompose',
      action: 'analyst_intervention',
      detail: { action: 'scope_confirmed', scopeContract },
    });

    // Build collection plan
    const investigation = await getInvestigation(investigationId);
    if (!investigation) throw new Error('Investigation not found after scope confirmation');

    const deskConfig = getDeskConfig(investigation.deskId);
    if (!deskConfig) throw new Error('Desk config not found');

    // Register investigation scope for analysis bridge signals
    registerInvestigationScope({
      investigationId,
      pipelineState: 'collect',
      geographicBbox: scopeContract.geographicBounds?.bbox,
      keywords: [
        scopeContract.subject.description,
        ...scopeContract.subject.watchlistMatches,
        ...deskConfig.entityWatchlists.map(w => w.name),
      ].filter(Boolean),
    });

    const plan: CollectionPlan = buildCollectionPlan(
      investigationId,
      scopeContract,
      deskConfig,
      tierConfig,
    );

    // Persist collection tasks
    for (const task of plan.tasks) {
      await createCollectionTask(task);

      await addLogEntry({
        investigationId,
        state: 'collect',
        action: 'task_dispatched',
        detail: { taskId: task.id, sourceId: task.sourceId, query: task.query },
      });
    }

    // Transition to COLLECT
    await transition('collect', {
      tasksCreated: plan.tasks.length,
      totalSources: plan.totalSources,
      estimatedTokens: plan.estimatedTokens,
    });
  }

  async function completeCollection(): Promise<void> {
    if (currentState !== 'collect') {
      throw new Error(`Cannot complete collection: investigation is in state '${currentState}', expected 'collect'`);
    }

    // Gate: all tasks completed/errored, none pending or running
    const tasks = await getCollectionTasks(investigationId);
    const pendingOrRunning = tasks.filter(t => t.status === 'pending' || t.status === 'running');

    if (pendingOrRunning.length > 0) {
      throw new Error(`Cannot complete collection: ${pendingOrRunning.length} tasks still pending/running`);
    }

    const completedTasks = tasks.filter(t => t.status === 'completed');

    await addLogEntry({
      investigationId,
      state: 'collect',
      action: 'state_transition',
      detail: {
        totalTasks: tasks.length,
        completed: completedTasks.length,
        failed: tasks.filter(t => t.status === 'failed').length,
        timeout: tasks.filter(t => t.status === 'timeout').length,
      },
    });

    // Check resources before proceeding
    const resourceCheck = checkResourcesOrForceReview();
    if (!resourceCheck.canContinue && resourceCheck.recommendation === 'hard_stop') {
      await forceReview(`Collection stopped: ${resourceCheck.reason}`);
      return;
    }

    // Transition to EXTRACT
    await transition('extract');
  }

  async function runExtraction(): Promise<void> {
    if (currentState !== 'extract') {
      throw new Error(`Cannot run extraction: investigation is in state '${currentState}', expected 'extract'`);
    }

    const investigation = await getInvestigation(investigationId);
    if (!investigation?.scopeContract) {
      await transitionToError('No scope contract for extraction');
      return;
    }

    const deskConfig = getDeskConfig(investigation.deskId);
    if (!deskConfig) {
      await transitionToError('Desk config not found for extraction');
      return;
    }

    const tasks = await getCollectionTasks(investigationId);
    const completedTasks = tasks.filter(t => t.status === 'completed' && t.rawData);

    if (completedTasks.length === 0) {
      // No data collected — proceed to validate (which will produce empty graph)
      await transition('validate', { entitiesExtracted: 0, reason: 'no_raw_data' });
      return;
    }

    // Create pipeline components
    const relevanceFilter = createRelevanceFilter(adapter, deskConfig.priorityIndicators);
    const extractor = createEntityExtractor(adapter);
    const entityTypes = investigation.scopeContract.subject.type
      ? [investigation.scopeContract.subject.type as import('@/intelligence/entities/types').EntityType]
      : [];

    // Add common types
    const allEntityTypes = new Set(entityTypes);
    allEntityTypes.add('LOCATION' as import('@/intelligence/entities/types').EntityType);
    allEntityTypes.add('SOURCE' as import('@/intelligence/entities/types').EntityType);
    allEntityTypes.add('ORGANIZATION' as import('@/intelligence/entities/types').EntityType);
    allEntityTypes.add('PERSON' as import('@/intelligence/entities/types').EntityType);
    allEntityTypes.add('EVENT' as import('@/intelligence/entities/types').EntityType);

    let totalExtracted = 0;

    for (const task of completedTasks) {
      // Check resources before each task
      const resourceCheck = checkResourcesOrForceReview();
      if (!resourceCheck.canContinue && resourceCheck.recommendation === 'hard_stop') {
        await forceReview(`Extraction stopped: ${resourceCheck.reason}`);
        return;
      }

      const rawText = task.rawData!;

      try {
        // Step 1: Relevance filter
        const tokensBeforeFilter = tokenTracker.totalTokens;
        const filterResult = await relevanceFilter.filter(rawText);
        resourceManager.recordTokens(tokenTracker.totalTokens - tokensBeforeFilter);

        if (!filterResult.passed) {
          await addLogEntry({
            investigationId,
            state: 'extract',
            action: 'entity_rejected',
            detail: {
              taskId: task.id,
              reason: 'relevance_below_threshold',
              score: filterResult.relevance.score,
            },
          });
          resourceManager.recordInfoGain(0);
          continue;
        }

        // Step 2: Entity extraction
        const tokensBeforeExtract = tokenTracker.totalTokens;
        const extractionResult = await extractor.extract(
          rawText,
          {
            sourceUrl: String(task.query.url ?? task.query.endpoint ?? ''),
            collectorId: `collector_${task.sourceId}`,
            collectedAt: task.completedAt ?? new Date().toISOString(),
            sourceId: task.sourceId,
          },
          Array.from(allEntityTypes),
          deskConfig.ontologyExtensions,
        );
        resourceManager.recordTokens(tokenTracker.totalTokens - tokensBeforeExtract);

        // Store extracted entities
        let newEntities = 0;
        for (const extracted of extractionResult.extracted) {
          const entityData: Omit<Entity, 'id'> = {
            type: extracted.type,
            name: extracted.name,
            aliases: extracted.aliases ?? [],
            description: extracted.description ?? '',
            properties: extracted.properties ?? {},
            provenance: extracted.provenance,
            corroboration: {
              count: 1,
              sources: [extracted.provenance.sourceUrl],
              natoRating: 'F6', // Default until corroboration pass
            },
            sources: [],
            relationships: [],
            firstSeen: extracted.provenance.collectedAt,
            lastSeen: extracted.provenance.collectedAt,
            confidence: extracted.provenance.confidence,
            tags: [],
            notes: [],
            watchStatus: 'none',
          };

          const entity = await entityStore.create(entityData);
          await linkEntityToInvestigation(investigationId, entity.id);
          newEntities++;
          totalExtracted++;

          eventBus.emit('investigation:entity-extracted', {
            investigationId,
            entity: entity as Entity,
          });

          await addLogEntry({
            investigationId,
            state: 'extract',
            action: 'entity_extracted',
            detail: {
              entityId: entity.id,
              entityName: entity.name,
              entityType: entity.type,
              taskId: task.id,
            },
          });
        }

        // Log rejections
        for (const rejected of extractionResult.rejected) {
          await addLogEntry({
            investigationId,
            state: 'extract',
            action: 'entity_rejected',
            detail: {
              entity: rejected.entity,
              reason: rejected.reason,
              taskId: task.id,
            },
          });
        }

        // Info gain tracking
        const totalInTask = extractionResult.extracted.length + extractionResult.rejected.length;
        const infoGain = totalInTask > 0 ? newEntities / totalInTask : 0;
        resourceManager.recordInfoGain(infoGain);

      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        await addLogEntry({
          investigationId,
          state: 'extract',
          action: 'error',
          detail: { taskId: task.id, error: message },
        });
      }
    }

    // Update node count
    const entityIds = await getInvestigationEntityIds(investigationId);
    await updateInvestigation(investigationId, { nodeCount: entityIds.length });
    resourceManager.updateGraphCounts(entityIds.length, 0);

    // Transition to VALIDATE
    await transition('validate', { entitiesExtracted: totalExtracted });
  }

  async function buildGraph(): Promise<void> {
    if (currentState !== 'validate') {
      throw new Error(`Cannot build graph: investigation is in state '${currentState}', expected 'validate'`);
    }

    const investigation = await getInvestigation(investigationId);
    if (!investigation?.scopeContract) {
      await transitionToError('No scope contract for graph build');
      return;
    }

    const deskConfig = getDeskConfig(investigation.deskId);

    // Load all investigation entities (already validated during extraction)
    const entityIds = await getInvestigationEntityIds(investigationId);
    const entities: Entity[] = [];

    for (const eid of entityIds) {
      const entity = await entityStore.getById(eid);
      if (entity) entities.push(entity);
    }

    await addLogEntry({
      investigationId,
      state: 'validate',
      action: entities.length > 0 ? 'validation_pass' : 'validation_fail',
      detail: { totalEntities: entities.length },
    });

    // Identify relationships (if tier supports it and we have entities)
    let edgeCount = 0;

    if (tierConfig.relationshipMapping && entities.length >= 2) {
      const identifier = createRelationshipIdentifier(adapter);

      try {
        // Collect raw text context from completed tasks
        const tasks = await getCollectionTasks(investigationId);
        const contextParts: string[] = [];
        for (const task of tasks) {
          if (task.status === 'completed' && task.rawData) {
            contextParts.push(task.rawData.slice(0, 2000)); // Limit context per task
          }
        }
        const context = contextParts.join('\n\n---\n\n');

        const tokensBeforeRel = tokenTracker.totalTokens;
        const relResult = await identifier.identify(
          entities.map(e => ({ name: e.name, type: e.type })),
          context,
          deskConfig?.ontologyExtensions,
        );
        resourceManager.recordTokens(tokenTracker.totalTokens - tokensBeforeRel);

        // Validate relationships (deterministic code)
        const knownNames = new Set(entities.map(e => e.name.toLowerCase()));
        const relValidation = validateRelationships(
          relResult.identified,
          {
            sourceText: context,
            knownEntityNames: knownNames,
          },
        );

        // Store valid relationships — resolve names to entity IDs
        const ts = new Date().toISOString();
        for (const rel of relValidation.valid) {
          const sourceEntity = entities.find(e => e.name.toLowerCase() === rel.sourceEntity.toLowerCase());
          const targetEntity = entities.find(e => e.name.toLowerCase() === rel.targetEntity.toLowerCase());
          if (sourceEntity && targetEntity) {
            await entityStore.addRelationship({
              sourceEntityId: sourceEntity.id,
              targetEntityId: targetEntity.id,
              type: rel.type,
              confidence: rel.confidence,
              evidence: [{
                sourceId: 'investigation',
                eventId: investigationId,
                timestamp: ts,
                snippet: rel.evidenceQuote,
                confidence: rel.confidence,
              }],
              firstSeen: ts,
              lastSeen: ts,
            });
            edgeCount++;
          }
        }

        await addLogEntry({
          investigationId,
          state: 'validate',
          action: 'llm_call',
          detail: {
            purpose: 'relationship_identification',
            identified: relResult.identified.length,
            validated: relValidation.valid.length,
            rejected: relValidation.rejected.length,
            tokensUsed: relResult.tokensUsed,
          },
        });

      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        await addLogEntry({
          investigationId,
          state: 'validate',
          action: 'error',
          detail: { error: message, phase: 'relationship_identification' },
        });
      }
    }

    await updateInvestigation(investigationId, {
      nodeCount: entities.length,
      edgeCount,
    });
    resourceManager.updateGraphCounts(entities.length, edgeCount);

    // Transition to GRAPH_BUILD
    await transition('graph_build', {
      nodes: entities.length,
      edges: edgeCount,
    });
  }

  async function corroborate(): Promise<void> {
    if (currentState !== 'graph_build') {
      throw new Error(`Cannot corroborate: investigation is in state '${currentState}', expected 'graph_build'`);
    }

    const investigation = await getInvestigation(investigationId);
    if (!investigation?.scopeContract) {
      await transitionToError('No scope contract for corroboration');
      return;
    }

    // Skip corroboration if tier has 0 passes
    if (tierConfig.corroborationPasses === 0) {
      await transition('corroborate', { skipped: true, reason: 'tier_no_corroboration' });
      return;
    }

    // Load all investigation entities
    const entityIds = await getInvestigationEntityIds(investigationId);
    const entities: Entity[] = [];
    for (const eid of entityIds) {
      const entity = await entityStore.getById(eid);
      if (entity) entities.push(entity);
    }

    // Entity enrichment (Layer 3) — STANDARD and DEEP tiers only
    const deskConfig = getDeskConfig(investigation.deskId);
    if (deskConfig && entities.length > 0 && tierConfig.sourceFilter !== 'top_3_by_reliability') {
      try {
        const searxngUrl = deskConfig.searchConfig?.searxngUrl ?? 'http://localhost:8080';
        const maxQueries = tierConfig.sourceFilter === 'all_including_low_reliability' ? entities.length : 5;
        const enrichQueries = identifyEnrichmentQueries(entities);
        if (enrichQueries.length > 0) {
          const enrichResults = await runEnrichment(enrichQueries, searxngUrl, maxQueries);
          for (const result of enrichResults) {
            const entity = entities.find(e => e.id === result.entityId);
            if (entity) {
              entity.properties[result.field] = result.value;
              await entityStore.update(entity.id, { properties: entity.properties });
            }
          }
          await addLogEntry({
            investigationId,
            state: 'graph_build',
            action: 'entity_extracted',
            detail: { method: 'enrichment', queriesRun: enrichQueries.length, resultsFound: enrichResults.length },
          });
        }
      } catch (err) {
        console.warn('[orchestrator] Entity enrichment failed:', err);
      }
    }

    // Run corroboration engine — cross-reference entities across sources,
    // compute NATO credibility ratings (deterministic, no LLM)
    if (deskConfig && entities.length > 0) {
      const corroborationResult = await runCorroboration(entities, deskConfig, investigationId);

      // Persist updated corroboration data back to entity store
      for (const updatedEntity of corroborationResult.entities) {
        await entityStore.update(updatedEntity.id, {
          corroboration: updatedEntity.corroboration,
        });
      }

      await addLogEntry({
        investigationId,
        state: 'corroborate',
        action: 'state_transition',
        detail: {
          corroborationPasses: tierConfig.corroborationPasses,
          totalEntities: corroborationResult.stats.totalEntities,
          corroborated: corroborationResult.stats.corroborated,
          singleSource: corroborationResult.stats.singleSource,
          weakMatches: corroborationResult.stats.weakMatches,
          reviewRequired: corroborationResult.reviewRequired.length,
        },
      });
    }

    // Compute coverage
    const coverage = computeCoverage(investigation.scopeContract, entityIds.length, tierConfig.sufficiencyThreshold);

    await updateInvestigation(investigationId, { coverage });
    resourceManager.updateCoverage(coverage);

    eventBus.emit('investigation:coverage-update', {
      investigationId,
      coverage,
    });

    await transition('corroborate', {
      coveragePercent: coverage.overallPercentage,
      sufficientForSynthesis: coverage.sufficientForSynthesis,
    });
  }

  async function synthesize(): Promise<void> {
    if (currentState !== 'corroborate') {
      throw new Error(`Cannot synthesize: investigation is in state '${currentState}', expected 'corroborate'`);
    }

    // Skip synthesis if tier doesn't support it
    if (!tierConfig.synthesis) {
      await transition('synthesize', { skipped: true, reason: 'tier_no_synthesis' });
      return;
    }

    const investigation = await getInvestigation(investigationId);
    if (!investigation) {
      await transitionToError('Investigation not found for synthesis');
      return;
    }

    const deskConfig = getDeskConfig(investigation.deskId);
    if (!deskConfig) {
      await transitionToError('Desk config not found for synthesis');
      return;
    }

    // Load all entities linked to this investigation
    const entityIds = await getInvestigationEntityIds(investigationId);
    const entities: Entity[] = [];
    for (const eid of entityIds) {
      const entity = await entityStore.getById(eid);
      if (entity) entities.push(entity);
    }

    // Collect relationships from entities
    const allRelationships: import('@/intelligence/entities/types').Relationship[] = [];
    const seenRelIds = new Set<string>();
    for (const entity of entities) {
      const rels = await entityStore.getRelationships(entity.id);
      for (const rel of rels) {
        if (!seenRelIds.has(rel.id)) {
          seenRelIds.add(rel.id);
          allRelationships.push(rel);
        }
      }
    }

    try {
      const { runSynthesis } = await import('@/intelligence/ai/report-writer');
      const draftAssessment = await runSynthesis(
        adapter,
        investigationId,
        entities,
        allRelationships,
        investigation.coverage,
        deskConfig,
      );

      // Log synthesis completion to audit trail
      await addLogEntry({
        investigationId,
        state: 'synthesize',
        action: 'synthesis_complete',
        detail: {
          modelUsed: draftAssessment.modelUsed,
          tokenCost: draftAssessment.tokenCost,
          sectionCount: draftAssessment.sections.length,
          generatedAt: draftAssessment.generatedAt,
        },
      });

      // Emit event for UI (graph panel picks this up)
      eventBus.emit('investigation:synthesis-complete', {
        investigationId,
        assessment: draftAssessment,
      });

    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('[Sentinel] Synthesis failed:', message);

      await addLogEntry({
        investigationId,
        state: 'synthesize',
        action: 'error',
        detail: { error: `Synthesis failed: ${message}` },
      });

      // Synthesis failure is not fatal — proceed to review with partial results
    }

    await transition('synthesize');
  }

  async function completeToReview(): Promise<void> {
    if (currentState !== 'synthesize') {
      throw new Error(`Cannot complete to review: investigation is in state '${currentState}', expected 'synthesize'`);
    }

    await transition('review', { completionType: 'normal' });

    // Unregister from analysis bridge
    unregisterInvestigationScope(investigationId);

    // Run provenance audit on extracted entities
    const entityIds = await getInvestigationEntityIds(investigationId);
    const entities = (await Promise.all(entityIds.map(id => entityStore.getById(id)))).filter(Boolean) as Entity[];
    if (entities.length > 0) {
      try {
        const auditReport = await auditProvenance(entities);
        await addLogEntry({
          investigationId,
          state: 'review',
          action: 'context_signal',
          detail: {
            signalType: 'provenance_audit',
            overallScore: auditReport.overallScore,
            verifiedCount: auditReport.verifiedCount,
            unverifiableCount: auditReport.unverifiableCount,
          },
        });
      } catch (err) {
        console.warn('[orchestrator] Provenance audit failed:', err);
      }
    }

    const investigation = await getInvestigation(investigationId);
    if (investigation) {
      eventBus.emit('investigation:complete', investigation);
    }
  }

  async function forceReview(reason: string): Promise<void> {
    if (currentState === 'review' || currentState === 'error') return;

    await addLogEntry({
      investigationId,
      state: currentState,
      action: 'resource_warning',
      detail: { reason, forcedReview: true },
    });

    await transition('review', { completionType: 'forced', reason });

    // Unregister from analysis bridge
    unregisterInvestigationScope(investigationId);

    const investigation = await getInvestigation(investigationId);
    if (investigation) {
      eventBus.emit('investigation:complete', investigation);
    }
  }

  async function runCollectionAndExtraction(): Promise<void> {
    if (currentState !== 'collect') {
      throw new Error(
        `Cannot run progressive pipeline: investigation is in state '${currentState}', expected 'collect'`,
      );
    }

    const investigation = await getInvestigation(investigationId);
    if (!investigation?.scopeContract) {
      await transitionToError('No scope contract for collection');
      return;
    }

    const deskConfig = getDeskConfig(investigation.deskId);
    if (!deskConfig) {
      await transitionToError('Desk config not found for collection');
      return;
    }

    try {
      const result = await runProgressivePipeline(investigationId, {
        concurrency: 3,
        taskTimeoutMs: 30_000,
      });

      // Pipeline already tracked tokens internally via its own tokenTracker/resourceManager.
      // Just sync the final totals to the orchestrator's state for accurate reporting.
      const entityIds = await getInvestigationEntityIds(investigationId);
      await updateInvestigation(investigationId, {
        nodeCount: entityIds.length,
        tokenUsage: result.tokensUsed,
      });
      resourceManager.updateGraphCounts(entityIds.length, 0);

      // Log progressive pipeline completion
      await addLogEntry({
        investigationId,
        state: 'collect',
        action: 'state_transition',
        detail: {
          pipeline: 'progressive',
          tasksCompleted: result.tasksCompleted,
          tasksFailed: result.tasksFailed,
          entitiesExtracted: result.entitiesExtracted,
          entitiesRejected: result.entitiesRejected,
          tokensUsed: result.tokensUsed,
          resourceLimitHit: result.resourceLimitHit,
        },
      });

      // Check if resource limit was hit
      if (result.resourceLimitHit) {
        await forceReview(`Progressive pipeline stopped: ${result.reason ?? 'resource limits reached'}`);
        return;
      }

      // Transition through EXTRACT → VALIDATE (progressive pipeline already did extraction)
      await transition('extract', { progressive: true, entitiesExtracted: result.entitiesExtracted });
      await transition('validate', { progressive: true, entitiesExtracted: result.entitiesExtracted });

    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      await transitionToError(`Progressive pipeline failed: ${message}`);
    }
  }

  async function transitionToError(error: string): Promise<void> {
    const preErrorState = currentState;

    await addLogEntry({
      investigationId,
      state: preErrorState,
      action: 'error',
      detail: { error },
    });

    await transition('error', { error });

    eventBus.emit('investigation:error', {
      investigationId,
      error,
      state: preErrorState,
    });
  }

  return {
    investigationId,
    getState: () => currentState,
    getInvestigation: () => getInvestigation(investigationId),
    getResourceManager: () => resourceManager,
    getTokenTracker: () => tokenTracker,
    start,
    confirmScope,
    completeCollection,
    runCollectionAndExtraction,
    runExtraction,
    buildGraph,
    corroborate,
    synthesize,
    completeToReview,
    forceReview,
    transitionToError,
  };
}

// ---------------------------------------------------------------------------
// Coverage computation (deterministic)
// ---------------------------------------------------------------------------

function computeCoverage(
  scope: ScopeContract,
  entityCount: number,
  sufficiencyThreshold: number = 0.8,
): CoverageReport {
  const parameters: CoverageParameter[] = [];

  // Subject identified?
  parameters.push({
    name: 'Subject identified',
    addressed: entityCount > 0,
    corroborated: false, // Updated by corroboration engine in S11
    detail: entityCount > 0 ? `${entityCount} entities found` : 'No entities found',
  });

  // Behavior indicators addressed?
  for (const indicator of scope.behaviorIndicators) {
    parameters.push({
      name: `Indicator: ${indicator.type}`,
      addressed: entityCount > 0, // Simplified — real check in S11
      corroborated: false,
      detail: indicator.threshold,
    });
  }

  // Geographic bounds confirmed?
  parameters.push({
    name: 'Geographic bounds',
    addressed: true, // Collection was constrained by desk config
    corroborated: true,
    detail: `bbox: [${scope.geographicBounds.bbox.join(', ')}]`,
  });

  // Temporal bounds within range?
  parameters.push({
    name: 'Temporal bounds',
    addressed: true,
    corroborated: true,
    detail: `${scope.temporalBounds.start} to ${scope.temporalBounds.end}`,
  });

  const addressed = parameters.filter(p => p.addressed).length;
  const total = parameters.length;
  const overallPercentage = total > 0 ? Math.round((addressed / total) * 100) : 0;

  return {
    parameters,
    overallPercentage,
    sufficientForSynthesis: overallPercentage >= sufficiencyThreshold * 100,
  };
}

// ---------------------------------------------------------------------------
// Resume from SQLite
// ---------------------------------------------------------------------------

/**
 * Resume an investigation from SQLite state.
 * Loads the investigation record and creates an orchestrator
 * in the saved state.
 */
export async function resumeInvestigation(
  investigationId: string,
): Promise<InvestigationOrchestrator | null> {
  const investigation = await getInvestigation(investigationId);
  if (!investigation) return null;

  // Don't resume completed or errored investigations
  if (investigation.state === 'review' || investigation.state === 'error') {
    return null;
  }

  return createOrchestrator(
    investigationId,
    investigation.tier,
    investigation.state,
    new Date(investigation.startedAt),
  );
}

// ---------------------------------------------------------------------------
// Start new investigation (convenience)
// ---------------------------------------------------------------------------

/**
 * Create and start a new investigation.
 * Returns the orchestrator in DECOMPOSE state (pending analyst confirmation).
 */
export async function startNewInvestigation(
  rawQuery: string,
  deskId: string,
  tier: InvestigationTierName = 'STANDARD',
): Promise<InvestigationOrchestrator> {
  const investigation = await createInvestigation({
    title: rawQuery.slice(0, 100),
    rawQuery,
    deskId,
    tier,
  });

  eventBus.emit('investigation:start', investigation);

  const orchestrator = createOrchestrator(investigation.id, tier);
  await orchestrator.start();

  return orchestrator;
}
